import React from 'react';
import { motion } from 'framer-motion';
import { FaCompass, FaDownload, FaCheckCircle, FaArrowRight, FaGift } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const AIAssessmentPromo = () => {
  return (
    <section className="px-[5%] py-16 md:py-20 lg:py-24">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-600 via-blue-700 to-cyan-800 shadow-2xl border border-teal-500/30"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-white rounded-full blur-2xl"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 p-8 md:p-12 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Side - Content */}
              <div className="text-center lg:text-left">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="inline-flex items-center gap-3 mb-6 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30"
                >
                  <FaGift className="text-yellow-300 text-lg" />
                  <span className="text-white font-semibold">FREE RESOURCE</span>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
                >
                  AI Strategy & Readiness Assessment
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                  className="text-xl text-white/90 mb-8 leading-relaxed"
                >
                  Evaluate your organization's AI maturity and build a roadmap for transformation. 
                  <span className="font-semibold text-yellow-300"> No signup required.</span>
                </motion.p>

                {/* Features */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  viewport={{ once: true }}
                  className="flex flex-wrap gap-4 mb-8 justify-center lg:justify-start"
                >
                  <div className="flex items-center gap-2 text-white/90">
                    <FaCheckCircle className="text-green-300" />
                    <span className="text-sm font-medium">Free Download</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90">
                    <FaCheckCircle className="text-green-300" />
                    <span className="text-sm font-medium">No Signup Required</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90">
                    <FaCheckCircle className="text-green-300" />
                    <span className="text-sm font-medium">Share Freely</span>
                  </div>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  viewport={{ once: true }}
                  className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                >
                  <Link to="/services/ai-readiness-assessment" className="no-underline">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-3 px-8 py-4 bg-white text-gray-900 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl"
                    >
                      <FaCompass className="text-xl" />
                      View Framework
                    </motion.button>
                  </Link>
                  
                  <Link to="/services/ai-assessment-form" className="no-underline">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="inline-flex items-center gap-3 px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-bold text-lg hover:bg-white hover:text-gray-900 transition-all"
                    >
                      <FaCompass className="text-xl" />
                      Take Assessment
                    </motion.button>
                  </Link>
                </motion.div>
              </div>

              {/* Right Side - Visual */}
              <div className="flex justify-center lg:justify-end">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  {/* Compass Icon */}
                  <div className="w-48 h-48 lg:w-64 lg:h-64 relative">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <div className="w-full h-full border-4 border-white/20 rounded-full"></div>
                    </motion.div>
                    
                    <motion.div
                      animate={{ rotate: -360 }}
                      transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-4 flex items-center justify-center"
                    >
                      <div className="w-full h-full border-2 border-white/30 rounded-full"></div>
                    </motion.div>

                    <div className="absolute inset-0 flex items-center justify-center">
                      <FaCompass className="text-6xl lg:text-8xl text-white drop-shadow-lg" />
                    </div>

                    {/* Floating Elements */}
                    <motion.div
                      animate={{ y: [-10, 10, -10] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                      className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-300 rounded-full flex items-center justify-center"
                    >
                      <FaCheckCircle className="text-gray-900 text-sm" />
                    </motion.div>

                    <motion.div
                      animate={{ y: [10, -10, 10] }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                      className="absolute -bottom-4 -left-4 w-6 h-6 bg-green-300 rounded-full flex items-center justify-center"
                    >
                      <FaArrowRight className="text-gray-900 text-xs" />
                    </motion.div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Bottom Accent */}
          <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-yellow-400 via-teal-400 to-blue-400"></div>
        </motion.div>
      </div>
    </section>
  );
};

export default AIAssessmentPromo;
